/**
 * Terms resolve system → member → member's book. Null at a level means inherit, not off.
 * Terms stop at the book: never store one per position.
 */
import { z } from 'zod';
import { DEFAULT_RISK_PER_TRADE } from '../../config.ts';

export interface Terms {
  /** Rupees one stop-out may cost; tickets are sized to it. */
  riskPerTrade: number;
  autopilot: boolean;
}

export type TermOverrides = { [K in keyof Terms]: Terms[K] | null };

export const TERM_NAMES = ['riskPerTrade', 'autopilot'] as const satisfies readonly (keyof Terms)[];

const eachTerm = <T>(value: (term: keyof Terms) => T) =>
  Object.fromEntries(TERM_NAMES.map((term) => [term, value(term)]));

export const NO_OVERRIDES = eachTerm(() => null) as TermOverrides;

/** Autopilot off: the desk never moves a member's money by default. */
export const SYSTEM_TERMS: Terms = {
  riskPerTrade: DEFAULT_RISK_PER_TRADE,
  autopilot: false,
};

export function resolveTerms(
  ...levels: readonly (Partial<TermOverrides> | null | undefined)[]
): Terms {
  const terms: Terms = { ...SYSTEM_TERMS };
  for (const level of levels) {
    for (const term of TERM_NAMES) {
      const said = level?.[term];
      if (said != null) Object.assign(terms, { [term]: said });
    }
  }
  return terms;
}

const riskSchema = z
  .number()
  .finite()
  .positive()
  .transform((rupees) => Math.round(rupees));

/** null clears the override (inherit); absent leaves it unchanged. */
export const termOverridesSchema = z.object({
  riskPerTrade: riskSchema.nullable().optional(),
  autopilot: z.boolean().nullable().optional(),
});
export type TermsAsked = z.infer<typeof termOverridesSchema>;

export const namesAnOverride = (asked: TermsAsked): boolean =>
  TERM_NAMES.some((term) => asked[term] !== undefined);

export const NO_TERM_GIVEN = 'nothing to set — give a risk per trade, an autopilot, or both';

export const overridesOf = (row: TermOverrides): TermOverrides =>
  eachTerm((term) => row[term]) as TermOverrides;

/** A term said the same as the level above is not said: it follows that level from then on. */
export const normalised = (said: TermOverrides, above: Terms): TermOverrides =>
  eachTerm((term) => (said[term] === above[term] ? null : said[term])) as TermOverrides;

export const applyAsked = (before: TermOverrides, asked: TermsAsked): TermOverrides =>
  eachTerm((term) => (asked[term] === undefined ? before[term] : asked[term])) as TermOverrides;
